import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { api, setToken, errMsg } from "@/lib/api";
import { usePhantom } from "@/hooks/usePhantom";
import { Header } from "@/components/Header";
import { KpiBar } from "@/components/KpiBar";
import { BotWalletCard } from "@/components/BotWalletCard";
import { RulesCard } from "@/components/RulesCard";
import { TradersTable } from "@/components/TradersTable";
import { TradesTable } from "@/components/TradesTable";
import { ActivityLog } from "@/components/ActivityLog";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import Landing from "@/pages/Landing";
import OwnerPanel from "@/pages/OwnerPanel";

export default function App() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [googleSession] = useState(() => {
    const m = window.location.hash.match(/session_id=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  });
  const [authed, setAuthed] = useState(() => {
    if (googleSession) return false;
    const t = localStorage.getItem("db_token");
    if (t) setToken(t);
    return !!t;
  });
  const [tab, setTab] = useState("dashboard");
  const googleDone = useRef(false);

  useEffect(() => {
    if (!googleSession || googleDone.current) return;
    googleDone.current = true;
    window.history.replaceState(null, "", window.location.pathname);
    api.post("/auth/google", { session_id: googleSession })
      .then(({ data }) => {
        setToken(data.token);
        setAuthed(true);
      })
      .catch((e) => toast.error(errMsg(e, "Google sign-in failed")))
      .finally(() => setGoogleBusy(false));
  }, [googleSession]);
  const [googleBusy, setGoogleBusy] = useState(!!googleSession);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setToken(null);
    setAuthed(false);
    setData(null);
    setTab("dashboard");
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
      setError(false);
    } catch (e) {
      if (e.response?.status === 401) {
        setToken(null);
        setAuthed(false);
        setData(null);
      } else setError(true);
    }
  }, []);

  const phantom = usePhantom();

  useEffect(() => {
    if (!authed) return;
    refresh();
    const id = setInterval(refresh, 10000);
    return () => clearInterval(id);
  }, [refresh, authed]);

  const onAuth = (token) => {
    setToken(token);
    setAuthed(true);
  };

  if (!authed) {
    return (
      <div className="grain min-h-screen">
        <Toaster theme="dark" position="bottom-right" />
        {googleBusy ? (
          <div className="py-40 text-center font-mono text-sm text-slate-500" data-testid="google-callback">Signing you in…</div>
        ) : (
          <Landing onAuth={onAuth} />
        )}
      </div>
    );
  }

  return (
    <div className="grain min-h-screen">
      <Toaster theme="dark" position="bottom-right" />
      {data && <Header settings={data.settings} me={data.me} onLogout={logout} refresh={refresh} tab={tab} setTab={setTab} />}
      <main className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-4">
        {error && <div className="panel px-4 py-2 text-xs font-mono text-[#EF4444]" data-testid="api-error">Backend unreachable. Retrying…</div>}
        {!data ? (
          <div className="py-32 text-center font-mono text-sm text-slate-500" data-testid="loading">Initializing engine…</div>
        ) : tab === "owner" && data.me.is_owner ? (
          <OwnerPanel />
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4 pb-2">
              <div>
                <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[0.95]">
                  Copy the top <span className="text-[#14F195]">1%</span>.<br className="hidden sm:block" /> {data.settings.trades_per_day ? `${data.settings.trades_per_day} shots a day.` : "Around the clock."}
                </h1>
                <p className="mt-3 text-sm text-slate-400 max-w-xl">
                  Mirrors buys from the highest-PnL Solana meme traders on GMGN.ai, Axiom and FOMO at ${data.settings.trade_usd.toFixed(2)} a trade, screens every token for rugs and honeypots, routes through Raydium or pump.fun, and sells each copy the moment the trader it copied sells.
                </p>
              </div>
            </div>
            {!data.subscription.active && (
              <div className="panel px-5 py-3 border-[#9945FF]/50 text-sm text-slate-300" data-testid="inactive-banner">
                Your copy engine is <span className="text-[#F59E0B] font-semibold">paused</span>. Subscribe for ${data.subscription.price_usd}/month in SOL to start copying.
              </div>
            )}
            <KpiBar data={data} />
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
              <div className="xl:col-span-8 space-y-4">
                <TradesTable trades={data.trades} refresh={refresh} />
                <TradersTable traders={data.traders} settings={data.settings} refresh={refresh} />
              </div>
              <div className="xl:col-span-4 space-y-4">
                <RulesCard settings={data.settings} refresh={refresh} />
                <SubscriptionCard sub={data.subscription} refresh={refresh} />
                <BotWalletCard wallet={data.bot_wallet} phantom={phantom} refresh={refresh} me={data.me} />
                <ActivityLog logs={data.logs} />
              </div>
            </div>
            <p className="text-[10px] font-mono text-slate-500 pt-4">
              Not financial advice. Meme coins are highly volatile; copy trading can lose 100% of funds.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
